import axios from "axios";
import { createEnvironmentURL } from "../utilities/environment";
import { getAuthToken } from "../constants/config/authentication";

const axiosConfig = {
  baseURL: `${createEnvironmentURL( 'myapi', 'corpnt' )}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  validateStatus: (status) => status < 400,
  mode: "cors",
};

// Create an Axios instance with custom headers
const axiosInstance = axios.create(axiosConfig);

// Ensure the latest token is included in the Authorization header for each request
axiosInstance.interceptors.request.use( ( config ) => {
  const token = getAuthToken();

  if ( token ) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

/**
 * @function
 * @constant
 * @param {string} requestType - The type of HTTP request (e.g., "GET", "POST", "DELETE").
 * @param {string} url - The URL endpoint for the API request.
 * @param {object} body - The payload or data to include in the request body (used for POST requests).
 * @param {function} successCallback - A callback function to handle a successful response.
 * @param {function} errorCallback - A callback function to handle errors or unsuccessful API requests.
 * @param {function} dispatch - A function for dispatching actions in a Redux or state management context (optional).
 * @param {AbortSignal} signal - An AbortSignal object that allows you to abort the request (optional).
 * @description
 *   This function makes generic API requests using Axios.
 *   It supports various HTTP request types and handles both successful responses and errors.
 *   The provided callbacks allow for custom handling of the response data or errors.
 * @throws {Error} If an unsupported request type is provided.
 */
export const baseAxiosAPI = async (
  requestType,
  url,
  body,
  successCallback,
  errorCallback,
  dispatch,
  signal,
) => {
  try {
    let response;

    switch (requestType) {
      case "GET":
        response = await axiosInstance.get( url, { signal } );
        break;
      case "POST":
        response = await axiosInstance.post(url, body);
        break;
      case "DELETE":
        response = await axiosInstance.delete(url);
        break;
      default:
        throw new Error("Unsupported request type");
    }

    if (response) {
      successCallback(response.data);
    } else {
      errorHandler("Invalid response", errorCallback, dispatch);
    }
  } catch (e) {
    const isAborted =
      axios.isCancel(e) ||
      e?.code === "ERR_CANCELED" ||
      e?.name === "CanceledError";

    if (isAborted) {
      console.log( "Request aborted", e.message );
    } else {
      errorHandler(url, e, errorCallback, dispatch);
    }
  }
};

/**
 * @function
 * @param {string} url - The URL where the error occurred.
 * @param {object} error - The error object or response received from the API request.
 * @param {function} errorCallback - A callback function to handle errors or failed API requests.
 * @param {function} dispatch - A function for dispatching actions in a Redux or state management context (optional).
 * @description
 *   This function is responsible for handling and formatting errors that may occur during API requests.
 *   It ensures consistent error processing and logs error messages along with the URL context.
 *   The provided callbacks allow for custom handling of the error.
 */
const errorHandler = (url, error, errorCallback, dispatch) => {
  let errorM = "";
  if (error.response) {
    errorM = error.response.data["message"];
  } else if (error.request) {
    errorM = error.request["statusText"];
  } else {
    errorM = error;
  }
  console.log(url, "ERROR: ", errorM);
  errorCallback(errorM);
  return;
};